/**
 * useFollowUpTemplates
 * Carrega e persiste os templates de follow-up da organização.
 */
import { useState, useEffect, useCallback } from 'react';
import { useOrganization } from '../../../../contexts/OrganizationContext';
import { notify } from '../../../../utils/toast';
import * as followUpService from '../../../../services/modules/followUpService';
import { DEFAULT_FOLLOW_UP_DELAY } from '../followUp.constants';
import { legacyHtmlToWhatsAppText } from '../followUpWhatsAppFormat';
import type { FollowUpSheetPayload, FollowUpTemplate } from '../types';

type FollowUpTemplatePatch = Partial<
    Pick<FollowUpTemplate, 'delayValue' | 'delayUnit' | 'message' | 'mediaUrl' | 'isActive'>
>;

export type FollowUpTemplateDraft = Omit<FollowUpTemplate, 'organizationId'>;

function draftFromTemplate(template: FollowUpTemplate): FollowUpTemplateDraft {
    const { organizationId, ...rest } = template;
    return {
        ...rest,
        message: legacyHtmlToWhatsAppText(rest.message),
    };
}

export function useFollowUpTemplates() {
    const { currentOrganization } = useOrganization();
    const organizationId = currentOrganization?.id;

    const [templates, setTemplates] = useState<FollowUpTemplateDraft[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const loadTemplates = useCallback(async () => {
        if (!organizationId) {
            setTemplates([]);
            return;
        }

        setLoading(true);
        try {
            const data = await followUpService.listTemplates(organizationId);
            setTemplates((data ?? []).map(draftFromTemplate));
        } catch (error) {
            console.error('[useFollowUpTemplates] Erro ao carregar templates:', error);
            notify.error('Erro ao carregar os follow-ups.');
        } finally {
            setLoading(false);
        }
    }, [organizationId]);

    useEffect(() => {
        loadTemplates();
    }, [loadTemplates]);

    const create = useCallback(async (
        input: FollowUpSheetPayload,
        toast: { success: string; error: string } = {
            success: 'Follow-up criado.',
            error: 'Erro ao criar o follow-up.',
        },
    ) => {
        if (!organizationId) return;

        setSaving(true);
        try {
            const saved = await followUpService.createTemplate(organizationId, {
                position: templates.length + 1,
                delayValue: input.delayValue ?? DEFAULT_FOLLOW_UP_DELAY.delayValue,
                delayUnit: input.delayUnit ?? DEFAULT_FOLLOW_UP_DELAY.delayUnit,
                message: input.message,
                mediaUrl: input.mediaUrl ?? null,
                aiPersonalize: false,
            });
            setTemplates((prev) => [...prev, draftFromTemplate(saved)]);
            notify.success(toast.success);
        } catch (error) {
            console.error('[useFollowUpTemplates] Erro ao criar template:', error);
            notify.error(toast.error);
            throw error;
        } finally {
            setSaving(false);
        }
    }, [organizationId, templates.length]);

    const duplicate = useCallback(async (template: FollowUpTemplateDraft) => {
        await create(
            {
                message: template.message,
                mediaUrl: template.mediaUrl ?? null,
                delayValue: template.delayValue,
                delayUnit: template.delayUnit,
            },
            { success: 'Follow-up duplicado.', error: 'Erro ao duplicar o follow-up.' },
        );
    }, [create]);

    const update = useCallback(async (
        id: string,
        patch: FollowUpTemplatePatch,
        options?: { silent?: boolean },
    ) => {
        if (!organizationId) return;

        if (!options?.silent) setSaving(true);
        try {
            const saved = await followUpService.updateTemplate(organizationId, id, patch);
            setTemplates((prev) => prev.map((template) => (
                template.id === id ? draftFromTemplate(saved) : template
            )));
            if (!options?.silent) notify.success('Follow-up atualizado.');
        } catch (error) {
            console.error('[useFollowUpTemplates] Erro ao atualizar template:', error);
            notify.error('Erro ao atualizar o follow-up.');
            throw error;
        } finally {
            if (!options?.silent) setSaving(false);
        }
    }, [organizationId]);

    const remove = useCallback(async (id: string) => {
        if (!organizationId) return;

        setSaving(true);
        try {
            await followUpService.deleteTemplate(organizationId, id);
            setTemplates((prev) => prev.filter((template) => template.id !== id));
            notify.success('Follow-up apagado.');
        } catch (error) {
            console.error('[useFollowUpTemplates] Erro ao apagar template:', error);
            notify.error('Erro ao apagar o follow-up.');
            throw error;
        } finally {
            setSaving(false);
        }
    }, [organizationId]);

    return {
        templates,
        loading,
        saving,
        add: create,
        duplicate,
        update,
        remove,
    };
}
